import { Button, Stack, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import {
  getGetTeamSubscriptionQueryKey,
  useGetTeamSubscription,
  useOpenPortal,
} from "../../api/generated/endpoints/billing/billing";
import type { SubscriptionView, TeamRole } from "../../api/generated/model";
import { leaveFor } from "../browser";
import { ErrorAlert } from "../ErrorAlert";
import { managesBilling } from "../permissions";
import { useRefetchOnRefusal } from "../refusal";

// The team's subscription on Settings' Team tab, Classic's and Pro's (spec
// billing/manage-subscription): live's "Manage Subscription", which opens Stripe's Portal, and the
// words under the billing line that say when a cancelled plan ends.

/** Live's words for its button (Settings' `SubscriptionAction`). */
export const MANAGE_SUBSCRIPTION = "Manage Subscription";

/** Live's day, "Dec 4, 2026" (`DATE_FORMAT_DAY`, `MMM d, yyyy`), in the browser's time zone. */
const DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });

/**
 * A subscription to manage in Stripe's Portal. Live's Settings offers it for every status but its
 * inactive ones, `canceled` and `pending` (`INACTIVE_SUBSCRIPTION_STATUSES`), though live's route
 * and ours answer an ended one with a Checkout: where live's UI and backend disagree, the UI is
 * the spec.
 */
export function isManageable(
  subscription: SubscriptionView | null | undefined,
): subscription is SubscriptionView {
  return subscription?.status === "active" || subscription?.status === "past_due";
}

/**
 * What the team's subscription says to a member: the day a plan cancelled at its period's end
 * ends, or the day an ended one ended; and, to one who cannot manage it, who can. Nothing for a
 * team with no subscription, or one whose first payment is pending.
 */
export function subscriptionWords(subscription: SubscriptionView | null, manages: boolean): string[] {
  if (subscription === null) return [];
  const day = DAY.format(new Date(subscription.current_period_end));
  if (subscription.status === "canceled") return [`Your plan ended on ${day}.`];
  if (!isManageable(subscription)) return [];
  return [
    ...(subscription.cancel_at_period_end ? [`Your plan ends on ${day}. It will not renew.`] : []),
    ...(manages ? [] : ["Only the team's owner or an admin can manage the subscription."]),
  ];
}

/**
 * The team's subscription, as both pieces read it. Stripe's webhook can land after the person is
 * back from the Portal, and no stream event moves this read, so it is read again on coming back to
 * the tab (the app's reads do not follow focus unless they say so) and whenever the browser shows
 * a page it kept whole, Back from Stripe. Both pieces ask at once on that: one read answers both.
 */
function useTeamSubscription(teamId: string) {
  const queryClient = useQueryClient();
  useEffect(() => {
    const back = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      const queryKey = getGetTeamSubscriptionQueryKey(teamId);
      void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
    };
    window.addEventListener("pageshow", back);
    return () => window.removeEventListener("pageshow", back);
  }, [queryClient, teamId]);
  return useGetTeamSubscription(teamId, { query: { refetchOnWindowFocus: true } });
}

interface ManageSubscriptionProps {
  teamId: string;
  role: TeamRole;
  /** Where the page places it: Pro's header row pushes it to its end. */
  className?: string | undefined;
  /** Pro's own button look; without it, Classic's filled Mantine button, as live's. */
  buttonClassName?: string | undefined;
}

/**
 * Live's "Manage Subscription", beside Transactions on Settings' Team tab: for the owner and the
 * admins of a team whose subscription it can manage, Stripe's Portal, where the plan changes or
 * is cancelled at its period's end. A team with none, or whose plan ended, gets no button. A
 * refusal shows where it happened, and the scope and the subscription are read again.
 */
export function ManageSubscription(props: ManageSubscriptionProps) {
  const { teamId, role, className, buttonClassName } = props;
  const subscription = useTeamSubscription(teamId).data?.subscription;
  const refetch = useRefetchOnRefusal(getGetTeamSubscriptionQueryKey(teamId));
  const portal = useOpenPortal({
    mutation: { onSuccess: ({ url }) => leaveFor(url), onError: refetch },
  });
  // Back from Stripe, a page the browser kept whole (its back-forward cache) gets its button back,
  // and the subscription is read again (`useTeamSubscription`).
  const { reset } = portal;
  useEffect(() => {
    const back = (event: PageTransitionEvent) => {
      if (event.persisted) reset();
    };
    window.addEventListener("pageshow", back);
    return () => window.removeEventListener("pageshow", back);
  }, [reset]);
  const offered = managesBilling(role) && isManageable(subscription);
  if (!offered && !portal.isError) return null;
  // Once Stripe answered, the browser is on its way out: the button stays busy until it has gone.
  const busy = portal.isPending || portal.isSuccess;
  const open = () => portal.mutate({ teamId });
  return (
    <Stack gap={4} align="flex-end" className={className}>
      {!offered ? null : buttonClassName === undefined ? (
        <Button onClick={open} loading={busy}>
          {MANAGE_SUBSCRIPTION}
        </Button>
      ) : (
        <button type="button" className={buttonClassName} onClick={open} disabled={busy}>
          {MANAGE_SUBSCRIPTION}
        </button>
      )}
      {portal.isError ? <ErrorAlert error={portal.error} /> : null}
    </Stack>
  );
}

interface SubscriptionLineProps {
  teamId: string;
  role: TeamRole;
  /** Pro's own small text; without it, Classic's. */
  className?: string | undefined;
}

/** The subscription's words under the Team tab's billing line, each a line of its own. */
export function SubscriptionLine({ teamId, role, className }: SubscriptionLineProps) {
  const read = useTeamSubscription(teamId);
  if (read.isError) return <ErrorAlert error={read.error} />;
  if (read.data === undefined) return null;
  return subscriptionWords(read.data.subscription, managesBilling(role)).map((words) =>
    className === undefined ? (
      <Text key={words} size="sm">
        {words}
      </Text>
    ) : (
      <span key={words} className={className}>
        {words}
      </span>
    ),
  );
}
